import { Injectable, inject, effect } from '@angular/core';
import { InterpolationParameters, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Language } from '../account/account.model';
import { SettingsService } from '../account/settings/settings.service';
import { LocaleService } from './locale.service';

@Injectable({
  providedIn: 'root'
})
export class AppTranslateService {
  private translate = inject(TranslateService);
  private settingsService = inject(SettingsService);
  private localeService = inject(LocaleService);

  private defaultLang = 'en'
  private languageChangeSubject = new BehaviorSubject<string>(this.defaultLang);

  readonly lang$ = this.languageChangeSubject.pipe(
    map(lang => lang ?? this.defaultLang)
  );

  constructor() {
    this.translate.setFallbackLang(this.defaultLang)
    this.localeService.set(this.defaultLang)

    this.translate.onLangChange.subscribe(event => this.languageChangeSubject.next(event.lang));

    effect(() => {
      const lang = this.settingsService.settings()?.['lang'];
      if (lang) this.switchLanguage(lang, false);
    });
  }

  /**
   * Переключить язык
   */
  switchLanguage(lang: string, save = true): void {
    if (['ru', 'en'].includes(lang)) {
      this.translate.use(lang);
      this.localeService.set(lang);
      if (save) this.settingsService.saveSettings({ lang }).subscribe();
    } else {
      this.translate.use(this.defaultLang);
      this.localeService.set(this.defaultLang);
      if (save) this.settingsService.saveSettings({ lang: this.defaultLang }).subscribe();
    }
    localStorage.setItem('lang', lang);
  }

  /**
   * Получить текущий язык
   */
  getCurrentLanguage(): Language {
    return this.translate.getCurrentLang() as Language || this.defaultLang;
  }

  /**
   * Инициализировать язык из localStorage
   */
  initializeLanguage(): void {
    const storedLang = localStorage.getItem('lang')
    const searchParams = new URLSearchParams(window.location.search)
    const paramLang = searchParams.get('lang')
    const browserLang = this.translate.getBrowserLang()?.substring(0, 2)

    if (storedLang) {
      this.switchLanguage(storedLang, false)
    } else if (paramLang) {
      this.switchLanguage(paramLang, false)
    } else if (browserLang) {
      this.switchLanguage(browserLang, false)
    } else {
      this.switchLanguage(this.defaultLang, false)
    }
  }

  /**
   * Получить перевод
   */
  get(key: string, params?: InterpolationParameters): string {
    return this.translate.instant(key, params);
  }

  /**
   * Получить перевод как Observable
   */
  get$(key: string, params?: InterpolationParameters) {
    return this.translate.get(key, params);
  }

  /**
   * Получить Observable для перевода, который обновляется при смене языка
   */
  getTranslation$(key: string, params?: InterpolationParameters): Observable<string> {
    return this.languageChangeSubject.pipe(
      switchMap(() => this.translate.get(key, params))
    );
  }

  /**
   * Получить Observable для отслеживания смены языка
   */
  get languageChange$(): Observable<string> {
    return this.languageChangeSubject.asObservable();
  }
}
